import { adminApi } from './api';
import { ADMIN_CONFIG } from '../config';
import type { AdminUser } from '../lib/types';

export interface AdminLoginPayload {
  email: string;
  password: string;
}

const ADMIN_INFO_KEY = 'dd_admin_info';

const adminService = {
  // ─── Auth ─────────────────────────────────────────────────────────────────
  login: async (payload: AdminLoginPayload) => {
    const { data } = await adminApi.post('/auth/admin/login', payload);
    if (data.token) {
      localStorage.setItem(ADMIN_CONFIG.tokenKey, data.token);
    }
    if (data.admin) {
      localStorage.setItem(ADMIN_INFO_KEY, JSON.stringify(data.admin));
    }
    return data; // { token, admin }
  },

  logout: () => {
    localStorage.removeItem(ADMIN_CONFIG.tokenKey);
    localStorage.removeItem(ADMIN_INFO_KEY);
  },

  isAuthenticated: (): boolean => !!localStorage.getItem(ADMIN_CONFIG.tokenKey),

  getAdminInfo: (): AdminUser | null => {
    try {
      const stored = localStorage.getItem(ADMIN_INFO_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  // ─── Stats ────────────────────────────────────────────────────────────────
  getStats: async () => {
    const { data } = await adminApi.get('/admin/stats');
    return data; // { stats, charts }
  },

  // ─── Users ────────────────────────────────────────────────────────────────
  getUsers: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/users', { params });
    return data; // { users, total, page, pages }
  },

  getUser: async (id: string) => {
    const { data } = await adminApi.get(`/admin/users/${id}`);
    return data; // { user }
  },

  updateUser: async (id: string, payload: Record<string, unknown>) => {
    const { data } = await adminApi.put(`/admin/users/${id}`, payload);
    return data;
  },

  banUser: async (id: string, reason?: string) => {
    const { data } = await adminApi.put(`/admin/users/${id}/ban`, { reason });
    return data;
  },

  unbanUser: async (id: string) => {
    const { data } = await adminApi.put(`/admin/users/${id}/unban`);
    return data;
  },

  deleteUser: async (id: string) => {
    const { data } = await adminApi.delete(`/admin/users/${id}`);
    return data;
  },

  // ─── Profiles ─────��───────────────────────────────────────────────────────
  getProfiles: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/profiles', { params });
    return data;
  },

  verifyProfile: async (id: string, verificationType: string, approved: boolean) => {
    const { data } = await adminApi.put(`/admin/profiles/${id}/verify`, { verificationType, approved });
    return data;
  },

  // ─── Reports ──────────────────────────────────────────────────────────────
  getReports: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/reports', { params });
    return data;
  },

  resolveReport: async (id: string, action: string, notes?: string) => {
    const { data } = await adminApi.put(`/admin/reports/${id}/resolve`, { action, notes });
    return data;
  },

  // ─── Payouts ──────────────────────────────────────────────────────────────
  getPayouts: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/payouts', { params });
    return data;
  },

  processPayout: async (id: string, action: 'approve' | 'reject', notes?: string) => {
    const { data } = await adminApi.put(`/admin/payouts/${id}/process`, { action, notes });
    return data;
  },

  // ─── Success Stories ──────────────────────────────────────────────────────
  getStories: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/stories', { params });
    return data;
  },

  approveStory: async (id: string) => {
    const { data } = await adminApi.put(`/admin/stories/${id}/approve`);
    return data;
  },

  rejectStory: async (id: string, reason?: string) => {
    const { data } = await adminApi.put(`/admin/stories/${id}/reject`, { reason });
    return data;
  },

  deleteStory: async (id: string) => {
    const { data } = await adminApi.delete(`/admin/stories/${id}`);
    return data;
  },

  // ─── Support Tickets ──────────────────────────────────────────────────────
  getTickets: async (params: Record<string, string> = {}) => {
    const { data } = await adminApi.get('/admin/tickets', { params });
    return data;
  },

  replyToTicket: async (id: string, content: string) => {
    const { data } = await adminApi.post(`/admin/tickets/${id}/reply`, { content });
    return data;
  },

  resolveTicket: async (id: string) => {
    const { data } = await adminApi.put(`/admin/tickets/${id}/resolve`);
    return data;
  },

  // ─── Plans ────────────────────────────────────────────────────────────────
  getPlans: async () => {
    const { data } = await adminApi.get('/admin/plans');
    return data; // { plans }
  },

  createPlan: async (payload: Record<string, unknown>) => {
    const { data } = await adminApi.post('/admin/plans', payload);
    return data;
  },

  updatePlan: async (id: string, payload: Record<string, unknown>) => {
    const { data } = await adminApi.put(`/admin/plans/${id}`, payload);
    return data;
  },

  deletePlan: async (id: string) => {
    const { data } = await adminApi.delete(`/admin/plans/${id}`);
    return data;
  },

  // ─── Dev: clear all test data ─────────────────────────────────────────────
  clearAllData: async () => {
    const { data } = await adminApi.delete('/admin/clear-data');
    return data;
  },
};

export default adminService;